import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';

import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from '@/lib/icons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const SCALE_LABELS = {
  1: 'Not Applied',
  2: 'Minimally Applied',
  3: 'Partially Applied',
  4: 'Mostly Applied',
  5: 'Fully Applied'
};

function DetailedAssessmentForm({ 
  contributors = [],
  participantIdentifier, 
  submitResponseUrl, 
  csrfToken,
  onComplete 
}) {
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Ref for scrolling to sections
  const accordionRefs = useRef({});

  // Add useEffect to fetch existing responses when component mounts
  useEffect(() => {
    const fetchExistingResponses = async () => {
      if (!participantIdentifier || contributors.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);        
        // Fix the URL construction to avoid duplicating "responses" in the path
        // Get the base path without '/create_response' at the end
        const baseUrl = submitResponseUrl.split('/create_response')[0];
        // Don't append '/responses' again since baseUrl already has it
        const url = `${baseUrl}?participant_identifier=${encodeURIComponent(participantIdentifier)}`;
        
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch existing responses:', response.status);
          setIsLoading(false);
          return;
        }

        const data = await response.json();        
        // Transform the responses into the format we need
        const responseMap = {};
        if (data.responses && Array.isArray(data.responses)) {
          data.responses.forEach(resp => {
            responseMap[resp.factor_id] = resp.response_value;
          });
          setResponses(responseMap);
        }
      } catch (err) {
        console.error('Error fetching existing responses:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExistingResponses();
  }, [participantIdentifier, contributors, submitResponseUrl, csrfToken]);

  // Calculate completion percentage with proper null checks and validation
  const totalFactors = (contributors || []).reduce((sum, c) => {
    const factorCount = (c.factors || []).length;
    return sum + factorCount;
  }, 0);
  
  const completedFactors = Object.keys(responses).length;
  // Cap the completion percentage at 100%
  const rawCompletionPercentage = totalFactors > 0 ? Math.round((completedFactors / totalFactors) * 100) : 0;
  const completionPercentage = Math.min(rawCompletionPercentage, 100);

  // Check if all questions in the selected sections are complete
  const allSelectedQuestionsComplete = totalFactors > 0 && completedFactors === totalFactors;

  const handleRatingChange = async (factorId, value) => {
    setResponses(prev => ({ ...prev, [factorId]: value }));
    
    // Save the response immediately
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(submitResponseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          participantIdentifier,
          factorId,
          response: parseInt(value, 10)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save response');
      }
    } catch (err) {
      console.error('Error saving response:', err);
      setError('Failed to save your response. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add section expansion handler with scrolling
  const handleSectionExpand = (sectionId) => {
    // If clicking the currently expanded section, just collapse it
    if (expandedSection === sectionId) {
      setExpandedSection(null);
      return;
    }
    
    // Set the expanded section
    setExpandedSection(sectionId);
    
    // Schedule scrolling after the state update and DOM changes
    setTimeout(() => {
      if (accordionRefs.current[sectionId]) {
        // Get the element's position
        const element = accordionRefs.current[sectionId];
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 80; // Subtract 80px to account for fixed header
        
        // Scroll to the adjusted position
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md p-8">
          <CardContent className="flex flex-col items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading your assessment data...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!contributors || contributors.length === 0) {
    return (
      <div className="text-center p-8 bg-card rounded-lg shadow">
        <p className="text-gray-500">No assessment sections available.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl min-h-[calc(100vh-8rem)]">
      {/* Progress Header */}
      <div className="mb-6 bg-card p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-3xl">Detailed Assessment Progress</h2>
          <span className="text-sm font-medium">{completionPercentage}% Questions Answered</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-score-5 h-2 rounded-full transition-all duration-300" style={{ width: `${completionPercentage}%` }} />
        </div>
      </div>

      {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">{error}</div>}

      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-100 text-blue-800 px-4 py-2 rounded-md shadow flex items-center">
          <Loader2 className="animate-spin h-4 w-4 mr-2" />
          Saving...
        </div>
      )}

      {/* Rating Scale Legend */}
      <div className="mb-6 bg-card p-4 rounded-lg shadow">
        <h3 className="text-xl font-medium mb-2 text-gray-600">Rating Scale</h3>
        <div className="grid grid-cols-5 gap-2 text-center">
          {Object.entries(SCALE_LABELS).map(([value, label]) => (
            <div key={value} className="flex flex-col items-center">
              <span className="font-bold">{value}</span>
              <span className="text-xs">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <Accordion type="single" collapsible value={expandedSection} onValueChange={handleSectionExpand}>
        {contributors.map((contributor, index) => {
          const contributorFactors = contributor.factors || []
          const contributorProgress = contributorFactors.reduce((acc, factor) => acc + (responses[factor.id] ? 1 : 0), 0)
          const isContributorComplete = contributorProgress === contributorFactors.length && contributorFactors.length > 0
          const isContributorInProgress = contributorProgress > 0 && !isContributorComplete
          const isContributorNotStarted = contributorProgress === 0

          return (
            <AccordionItem key={contributor.id} value={contributor.id} className="bg-card mb-4 rounded-lg shadow" ref={(el) => (accordionRefs.current[contributor.id] = el)}>
              <AccordionTrigger className="px-4 py-3">
                <div className="flex justify-between items-center w-full pr-4">
                  <div>
                    <h3 className="text-xl font-medium text-left">{contributor.name}</h3>
                    <p className="text-sm text-gray-500 text-left">
                      {contributorProgress} of {contributorFactors.length} complete
                    </p>
                  </div>
                  {isContributorComplete && <span className="text-green-600 text-sm font-medium px-2 py-1 bg-green-100 rounded">✓ Complete</span>}
                  {isContributorInProgress && <span className="text-amber-600 text-sm font-medium px-2 py-1 bg-amber-100 rounded">⟳ In Progress</span>}
                  {isContributorNotStarted && <span className="text-slate-600 text-sm font-medium px-2 py-1 bg-slate-100 rounded">○ Not Started</span>}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-2">
                {contributorFactors.length > 0 ? (
                  <div className="space-y-6">
                    {contributorFactors.map((factor) => (
                      <div key={factor.id} className="border-b pb-4">
                        <Label className="text-base mb-3 block">{factor.text}</Label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(SCALE_LABELS).map(([value, label]) => {
                            const isSelected = responses[factor.id] === parseInt(value, 10)
                            return (
                              <Button key={value} type="button" variant={isSelected ? 'default' : 'outline'} className={cn('flex-1 py-2 min-w-[60px]', isSelected && 'ring-2 ring-primary')} onClick={() => handleRatingChange(factor.id, parseInt(value, 10))}>
                                {value}
                              </Button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No factors available for this section.</p>
                )}
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>

      {/* View Report button - disabled until all selected questions are answered */}
      <div className="mt-8 flex justify-center">
        <Button 
          onClick={onComplete}
          size="lg"
          className="px-8 py-6 text-lg"
          disabled={!allSelectedQuestionsComplete}
        >
          View Report
        </Button>
      </div>
    </div>
  )
}

DetailedAssessmentForm.propTypes = {
  contributors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    factors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  participantIdentifier: PropTypes.string.isRequired,
  submitResponseUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default DetailedAssessmentForm; 